import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { adminRequest } from '../../utils/adminRequest';
import { getImageSrc } from '../../utils/request';

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: 8,
  fontSize: 15,
  boxSizing: 'border-box'
};

const labelStyle = { display: 'block', marginBottom: 8, fontWeight: 600 };
const hintStyle = { fontSize: 12, color: '#6b7280', lineHeight: 1.5 };

function isImageFile(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || '');
}

function getImagePreviewSrc(url) {
  if (!url || typeof url !== 'string') return '';
  return getImageSrc(url);
}

function getDropZoneStyle(isActive, isBusy) {
  return {
    border: `2px dashed ${isActive ? '#6366f1' : '#d1d5db'}`,
    background: isActive ? 'rgba(99, 102, 241, 0.08)' : '#f8fafc',
    borderRadius: 12,
    padding: 16,
    cursor: isBusy ? 'progress' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: isBusy ? 0.75 : 1
  };
}

function AdminMemberForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreate = !id;
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [avatarDragOver, setAvatarDragOver] = useState(false);
  const [photosDragOver, setPhotosDragOver] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const avatarInputRef = useRef(null);
  const photosInputRef = useRef(null);
  const avatarDragDepth = useRef(0);
  const photosDragDepth = useRef(0);
  const [form, setForm] = useState({
    openid: '',
    nickname: '',
    avatar: '',
    gender: '',
    age: '',
    birthday: '',
    city: '',
    job: '',
    intro: '',
    ideal_type: '',
    wechat: '',
    height: '',
    education: '',
    school: '',
    annual_salary: '',
    hometown: '',
    zodiac: '',
    housing: '',
    car: '',
    marriage: '',
    dating_goal: '',
    status: 'active',
    bonus_chances: '',
    photosJson: '[]',
    tagsJson: '[]'
  });

  useEffect(() => {
    if (isCreate) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await adminRequest({ url: `/api/admin/members/${id}`, method: 'GET' });
        const u = res.data;
        if (cancelled || !u) return;
        setForm({
          openid: u.openid || '',
          nickname: u.nickname || '',
          avatar: u.avatar || '',
          gender: u.gender || '',
          age: u.age != null ? String(u.age) : '',
          birthday: u.birthday || '',
          city: u.city || '',
          job: u.job || '',
          intro: u.intro || '',
          ideal_type: u.ideal_type || '',
          wechat: u.wechat || '',
          height: u.height != null ? String(u.height) : '',
          education: u.education || '',
          school: u.school || '',
          annual_salary: u.annual_salary || '',
          hometown: u.hometown || '',
          zodiac: u.zodiac || '',
          housing: u.housing || '',
          car: u.car || '',
          marriage: u.marriage || '',
          dating_goal: u.dating_goal || '',
          status: u.status || 'active',
          bonus_chances: u.bonus_chances != null ? String(u.bonus_chances) : '0',
          photosJson: JSON.stringify(Array.isArray(u.photos) ? u.photos : [], null, 2),
          tagsJson: JSON.stringify(Array.isArray(u.tags) ? u.tags : [], null, 2)
        });
      } catch (err) {
        if (err.response?.status === 401) navigate('/admin');
        else {
          alert(err.response?.data?.error || '加载失败');
          navigate('/admin/members');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate, isCreate]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadAdminImage = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await adminRequest({
      url: '/api/admin/upload',
      method: 'POST',
      data: fd
    });
    if (!res.data?.url) {
      throw new Error(res.data?.error || '上传失败');
    }
    return res.data.url;
  }, []);

  const uploadAvatarFile = useCallback(async (file) => {
    if (!isImageFile(file)) {
      alert('请上传图片文件');
      return;
    }
    setUploadingAvatar(true);
    try {
      const url = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, avatar: url }));
    } catch (err) {
      alert(err.response?.data?.error || err.message || '上传失败');
    } finally {
      setUploadingAvatar(false);
    }
  }, [uploadAdminImage]);

  const uploadPhotoFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(isImageFile);
    if (files.length === 0) {
      alert('请选择图片文件');
      return;
    }

    try {
      const parsed = JSON.parse(form.photosJson || '[]');
      if (!Array.isArray(parsed)) throw new Error('photos 须为数组');
    } catch (err) {
      alert('相册 photos 不是合法 JSON 数组，请先修正后再上传');
      return;
    }

    setUploadingPhotos(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const url = await uploadAdminImage(file);
        uploadedUrls.push(url);
      }
      setForm((prev) => {
        let currentPhotos = [];
        try {
          const parsed = JSON.parse(prev.photosJson || '[]');
          currentPhotos = Array.isArray(parsed) ? parsed : [];
        } catch {
          currentPhotos = [];
        }
        return {
          ...prev,
          photosJson: JSON.stringify([...currentPhotos, ...uploadedUrls], null, 2)
        };
      });
    } catch (err) {
      alert(err.response?.data?.error || err.message || '上传失败');
    } finally {
      setUploadingPhotos(false);
    }
  }, [form.photosJson, uploadAdminImage]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await uploadAvatarFile(file);
    e.target.value = '';
  };

  const handlePhotosChange = async (e) => {
    const files = e.target.files;
    if (files?.length) await uploadPhotoFiles(files);
    e.target.value = '';
  };

  const onAvatarDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types?.includes?.('Files')) return;
    avatarDragDepth.current += 1;
    setAvatarDragOver(true);
  };

  const onAvatarDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    avatarDragDepth.current -= 1;
    if (avatarDragDepth.current <= 0) {
      avatarDragDepth.current = 0;
      setAvatarDragOver(false);
    }
  };

  const onAvatarDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onAvatarDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    avatarDragDepth.current = 0;
    setAvatarDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadAvatarFile(file);
  };

  const onPhotosDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types?.includes?.('Files')) return;
    photosDragDepth.current += 1;
    setPhotosDragOver(true);
  };

  const onPhotosDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    photosDragDepth.current -= 1;
    if (photosDragDepth.current <= 0) {
      photosDragDepth.current = 0;
      setPhotosDragOver(false);
    }
  };

  const onPhotosDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onPhotosDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    photosDragDepth.current = 0;
    setPhotosDragOver(false);
    if (e.dataTransfer.files?.length) uploadPhotoFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let photos;
    let tags;
    try {
      photos = JSON.parse(form.photosJson || '[]');
      if (!Array.isArray(photos)) throw new Error('photos 须为 JSON 数组');
    } catch (err) {
      alert('相册 photos 不是合法 JSON 数组');
      return;
    }
    try {
      tags = JSON.parse(form.tagsJson || '[]');
      if (!Array.isArray(tags)) throw new Error('tags 须为 JSON 数组');
    } catch (err) {
      alert('标签 tags 不是合法 JSON 数组');
      return;
    }

    setSaving(true);
    try {
      const payload = {
          nickname: form.nickname,
          avatar: form.avatar,
          gender: form.gender || null,
          age: form.age === '' ? null : form.age,
          birthday: form.birthday || null,
          city: form.city || null,
          job: form.job || null,
          intro: form.intro || null,
          ideal_type: form.ideal_type || null,
          wechat: form.wechat || null,
          height: form.height === '' ? null : form.height,
          education: form.education || null,
          school: form.school || null,
          annual_salary: form.annual_salary || null,
          hometown: form.hometown || null,
          zodiac: form.zodiac || null,
          housing: form.housing || null,
          car: form.car || null,
          marriage: form.marriage || null,
          dating_goal: form.dating_goal || null,
          status: form.status,
          bonus_chances: form.bonus_chances === '' ? 0 : form.bonus_chances,
          photos,
          tags
      };
      if (isCreate) {
        if (form.openid && form.openid.trim()) payload.openid = form.openid.trim();
        const res = await adminRequest({
          url: '/api/admin/members',
          method: 'POST',
          data: payload
        });
        const newId = res.data?.user?.id;
        alert('创建成功');
        if (newId) navigate(`/admin/members/${newId}/edit`);
        else navigate('/admin/members');
      } else {
        const { openid: _o, ...updateBody } = payload;
        await adminRequest({
          url: `/api/admin/members/${id}`,
          method: 'PUT',
          data: updateBody
        });
        alert('保存成功');
        navigate('/admin/members');
      }
    } catch (err) {
      alert(err.response?.data?.error || (isCreate ? '创建失败' : '保存失败'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>加载中...</div>;

  let photoPreviewUrls = [];
  try {
    const parsed = JSON.parse(form.photosJson || '[]');
    if (Array.isArray(parsed)) {
      photoPreviewUrls = parsed.filter((item) => typeof item === 'string' && item.trim());
    }
  } catch {}

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/admin/members" style={{ color: '#6366f1', textDecoration: 'none' }}>← 返回列表</Link>
        <h2 style={{ margin: 0 }}>{isCreate ? '新增会员' : `编辑会员 #${id}`}</h2>
      </div>
      <form
        onSubmit={handleSubmit}
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          maxWidth: 720
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          <div>
            <label style={labelStyle}>OpenID {isCreate ? '（可选，留空自动生成）' : '（只读）'}</label>
            <input
              style={{ ...inputStyle, background: isCreate ? '#fff' : '#f3f4f6' }}
              value={form.openid}
              readOnly={!isCreate}
              onChange={(e) => patch('openid', e.target.value)}
              placeholder={isCreate ? '例如线下导入的微信 openid' : ''}
            />
          </div>
          <div>
            <label style={labelStyle}>昵称</label>
            <input style={inputStyle} value={form.nickname} onChange={(e) => patch('nickname', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>头像</label>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
            <div
              style={getDropZoneStyle(avatarDragOver, uploadingAvatar)}
              onClick={() => !uploadingAvatar && avatarInputRef.current?.click()}
              onDragEnter={onAvatarDragEnter}
              onDragLeave={onAvatarDragLeave}
              onDragOver={onAvatarDragOver}
              onDrop={onAvatarDrop}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {form.avatar ? (
                  <img
                    src={getImagePreviewSrc(form.avatar)}
                    alt="头像预览"
                    style={{ width: 88, height: 88, borderRadius: 12, objectFit: 'cover', border: '1px solid #d1d5db', background: '#fff' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 88,
                      height: 88,
                      borderRadius: 12,
                      border: '1px dashed #cbd5e1',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8',
                      fontSize: 12,
                      textAlign: 'center',
                      padding: 8,
                      boxSizing: 'border-box'
                    }}
                  >
                    暂无头像
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 6 }}>
                    {uploadingAvatar ? '头像上传中...' : '点击选择图片，或直接拖拽到这里'}
                  </div>
                  <div style={hintStyle}>上传后会自动写入下方 URL，可继续手动修改。</div>
                </div>
              </div>
            </div>
            <input
              style={{ ...inputStyle, marginTop: 10 }}
              value={form.avatar}
              onChange={(e) => patch('avatar', e.target.value)}
              placeholder="支持 /uploads/... 或完整图片 URL"
            />
          </div>
          <div>
            <label style={labelStyle}>性别</label>
            <select style={inputStyle} value={form.gender} onChange={(e) => patch('gender', e.target.value)}>
              <option value="">未设置</option>
              <option value="male">男</option>
              <option value="female">女</option>
              <option value="unknown">未知</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>年龄</label>
            <input style={inputStyle} type="number" value={form.age} onChange={(e) => patch('age', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>生日</label>
            <input style={inputStyle} placeholder="YYYY-MM-DD" value={form.birthday} onChange={(e) => patch('birthday', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>身高 (cm)</label>
            <input style={inputStyle} type="number" value={form.height} onChange={(e) => patch('height', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>城市</label>
            <input style={inputStyle} value={form.city} onChange={(e) => patch('city', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>家乡</label>
            <input style={inputStyle} value={form.hometown} onChange={(e) => patch('hometown', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>职业</label>
            <input style={inputStyle} value={form.job} onChange={(e) => patch('job', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>学历</label>
            <input style={inputStyle} value={form.education} onChange={(e) => patch('education', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>学校</label>
            <input style={inputStyle} value={form.school} onChange={(e) => patch('school', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>年薪</label>
            <input style={inputStyle} value={form.annual_salary} onChange={(e) => patch('annual_salary', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>星座</label>
            <input style={inputStyle} value={form.zodiac} onChange={(e) => patch('zodiac', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>房产</label>
            <input style={inputStyle} value={form.housing} onChange={(e) => patch('housing', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>车</label>
            <input style={inputStyle} value={form.car} onChange={(e) => patch('car', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>婚况</label>
            <input style={inputStyle} value={form.marriage} onChange={(e) => patch('marriage', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>脱单目标</label>
            <input style={inputStyle} value={form.dating_goal} onChange={(e) => patch('dating_goal', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>微信号</label>
            <input style={inputStyle} value={form.wechat} onChange={(e) => patch('wechat', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>账户状态</label>
            <select style={inputStyle} value={form.status} onChange={(e) => patch('status', e.target.value)}>
              <option value="active">active（正常）</option>
              <option value="frozen">frozen（冻结）</option>
              <option value="deleted">deleted</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>赠送牵手次数 bonus_chances</label>
            <input style={inputStyle} type="number" min="0" value={form.bonus_chances} onChange={(e) => patch('bonus_chances', e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <label style={labelStyle}>自我介绍</label>
          <textarea style={{ ...inputStyle, minHeight: 100 }} value={form.intro} onChange={(e) => patch('intro', e.target.value)} />
        </div>
        <div style={{ marginTop: 20 }}>
          <label style={labelStyle}>理想型</label>
          <input style={inputStyle} value={form.ideal_type} onChange={(e) => patch('ideal_type', e.target.value)} />
        </div>
        <div style={{ marginTop: 20 }}>
          <label style={labelStyle}>相册 photos（JSON 数组，URL 字符串）</label>
          <input
            ref={photosInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotosChange}
            style={{ display: 'none' }}
          />
          <div
            style={{ ...getDropZoneStyle(photosDragOver, uploadingPhotos), marginBottom: 12 }}
            onClick={() => !uploadingPhotos && photosInputRef.current?.click()}
            onDragEnter={onPhotosDragEnter}
            onDragLeave={onPhotosDragLeave}
            onDragOver={onPhotosDragOver}
            onDrop={onPhotosDrop}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              {uploadingPhotos ? '相册上传中...' : '点击选择多张图片，或把图片直接拖进来'}
            </div>
            <div style={{ ...hintStyle, marginTop: 6 }}>
              上传完成后会自动追加到下方 `photosJson`。
            </div>
            {photoPreviewUrls.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 10, marginTop: 14 }}>
                {photoPreviewUrls.map((url, idx) => (
                  <img
                    key={`${url}-${idx}`}
                    src={getImagePreviewSrc(url)}
                    alt={`相册 ${idx + 1}`}
                    style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 10, border: '1px solid #d1d5db', background: '#fff' }}
                  />
                ))}
              </div>
            )}
          </div>
          <textarea style={{ ...inputStyle, minHeight: 120, fontFamily: 'monospace' }} value={form.photosJson} onChange={(e) => patch('photosJson', e.target.value)} />
        </div>
        <div style={{ marginTop: 20 }}>
          <label style={labelStyle}>标签 tags（JSON 数组）</label>
          <textarea style={{ ...inputStyle, minHeight: 80, fontFamily: 'monospace' }} value={form.tagsJson} onChange={(e) => patch('tagsJson', e.target.value)} />
        </div>
        <div style={{ marginTop: 28, display: 'flex', gap: 12 }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 24px',
              background: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? (isCreate ? '创建中...' : '保存中...') : isCreate ? '创建' : '保存'}
          </button>
          <Link
            to="/admin/members"
            style={{
              padding: '12px 24px',
              background: '#eee',
              color: '#333',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600,
              display: 'inline-block'
            }}
          >
            取消
          </Link>
        </div>
      </form>
    </div>
  );
}

export default AdminMemberForm;
